import { useState, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import Input from '../../../components/Input/Input'
import Button from '../../../components/Button/Button'
import Calendar from '../../../components/Calendar/Calendar'
import Dropdown from '../../../components/Dropdown/Dropdown'
import Switch from '../../../components/Switch/Switch'
import CoApplicantSearch from '../../../components/CoApplicantSearch/CoApplicantSearch'
import { validateEmail } from '../../../helpers/validationEmail'
import { phoneNumber, generateSSNumber, ssnForPrefill } from '../../../utils/payment'
import { usePlacesWidget } from 'react-google-autocomplete'
import { GOOGLE_MAPS_API_KEY } from '../../../config/consts'
import { fetchStates } from '../../../features/states/states'

const PersonalInfoStep = ({
  personalInfo, setPersonalInfo, onNext,
  hasCoApplicant, setHasCoApplicant,
  coApplicantMode, setCoApplicantMode,
  coApplicantInfo, setCoApplicantInfo,
  linkedCoApplicantId, setLinkedCoApplicantId,
}) => {
  const dispatch = useDispatch()
  const [errors, setErrors] = useState({})
  const [addressInput, setAddressInput] = useState(personalInfo?.address || '')
  const [states, setStates] = useState([])

  useEffect(() => {
    const getStates = async () => {
      const response = await dispatch(fetchStates({ searchQuery: '' }))
      setStates(response?.payload?.data || [])
    }
    getStates()
  }, [dispatch])

  const handleChange = (field, value) => {
    if (field === 'primaryPhone') {
      value = phoneNumber(value)
    }
    if (field === 'ssn') {
      value = generateSSNumber(value)
    }
    if (field === 'firstName' || field === 'lastName') {
      value = value.replace(/[^a-zA-Z'\s-]/g, '')
    }
    if (field === 'zip') {
      value = value?.replace(/\D/g, '')
    }
    setPersonalInfo(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const handleAddressSelect = useCallback((place) => {
    const streetNumber = place.address_components.find(c => c.types.includes("street_number"))?.long_name || ""
    const route = place.address_components.find(c => c.types.includes("route"))?.long_name || ""
    const fullAddress = streetNumber ? `${streetNumber} ${route}` : route

    setPersonalInfo(prev => ({
      ...prev,
      address: fullAddress,
      city: place.address_components.find(c => c.types.includes("locality"))?.long_name || "",
      state: place.address_components.find(c => c.types.includes("administrative_area_level_1"))?.short_name || "",
      zip: place.address_components.find(c => c.types.includes("postal_code"))?.long_name || ""
    }))
    setAddressInput(place.formatted_address)
  }, [setPersonalInfo])

  const { ref: addressRef } = usePlacesWidget({
    apiKey: GOOGLE_MAPS_API_KEY,
    onPlaceSelected: handleAddressSelect,
    options: {
      types: ['address'],
      componentRestrictions: { country: 'us' },
    },
  })

  const handleCoApplicantChange = (field, value) => {
    if (field === 'primaryPhone') value = phoneNumber(value)
    if (field === 'ssNumber') value = generateSSNumber(value)
    if (['firstName', 'lastName', 'middleName'].includes(field)) {
      value = value?.replace(/[^a-zA-Z']/g, '')
    }
    setCoApplicantInfo(prev => ({ ...prev, [field]: value }))
  }

  const handleSelectExisting = (coApplicant) => {
    setLinkedCoApplicantId(coApplicant._id)
    setCoApplicantInfo({
      firstName: coApplicant.firstName || '',
      lastName: coApplicant.lastName || '',
      middleName: coApplicant.middleName || '',
      email: coApplicant.email || '',
      primaryPhone: coApplicant.primaryPhone ? phoneNumber(coApplicant.primaryPhone) : '',
      ssNumber: ssnForPrefill(coApplicant.ssNumber),
      dateOfBirth: coApplicant.dateOfBirth || '',
      gender: coApplicant.gender || '',
      address: coApplicant.address?.address || '',
      aptNumber: coApplicant.address?.aptNumber || '',
      city: coApplicant.address?.city || '',
      state: coApplicant.address?.state || '',
      zip: coApplicant.address?.zip || '',
    })
  }

  const validate = () => {
    const newErrors = {}
    if (!personalInfo.firstName?.trim()) newErrors.firstName = 'First name is required'
    if (!personalInfo.lastName?.trim()) newErrors.lastName = 'Last name is required'
    if (!personalInfo.email?.trim()) newErrors.email = 'Email is required'
    else if (!validateEmail(personalInfo.email)) newErrors.email = 'Invalid email format'
    if (!personalInfo.primaryPhone?.trim()) newErrors.primaryPhone = 'Phone is required'
    else if (personalInfo.primaryPhone.replace(/\D/g, '').length < 10) newErrors.primaryPhone = 'Invalid phone number'
    if (!personalInfo.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required'
    if (!personalInfo.ssn?.trim()) newErrors.ssn = 'SSN is required for credit check'
    else if (personalInfo.ssn.replace(/\D/g, '').length !== 9) newErrors.ssn = 'SSN must be 9 digits'

    if (hasCoApplicant && coApplicantMode === 'create') {
      if (!coApplicantInfo.firstName?.trim()) newErrors.coFirstName = 'Required'
      if (!coApplicantInfo.lastName?.trim()) newErrors.coLastName = 'Required'
      if (!coApplicantInfo.dateOfBirth) newErrors.coDateOfBirth = 'Required'
    }
    if (hasCoApplicant && coApplicantMode === 'link' && !linkedCoApplicantId) {
      newErrors.coSearch = 'Select a co-applicant'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validate()) {
      onNext()
    }
  }

  return (
    <>
      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full">
        <div className="font-bold text-lg md:text-xl-20 pt-4 md:pt-6 pl-4 md:pl-12 pb-3 md:pb-4">
          Personal Information
        </div>
        <div className="border-b border-divider mx-4 md:mx-6" />
        <div className="flex flex-col md:flex-row px-4 md:px-12 pt-4 md:pt-6 gap-4">
          <div className="flex-1">
            <Input
              placeholder="Enter First Name"
              label="First name *"
              value={personalInfo.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              errorMsg={errors.firstName}
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder="Enter Last Name"
              label="Last name *"
              value={personalInfo.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              errorMsg={errors.lastName}
            />
          </div>
        </div>
        <div className="flex flex-col md:flex-row px-4 md:px-12 py-4 md:py-6 gap-4">
          <div className="flex-1">
            <Input
              type="email"
              placeholder="Enter Email"
              label="Email *"
              value={personalInfo.email}
              onChange={(e) => handleChange('email', e.target.value)}
              errorMsg={errors.email}
            />
          </div>
          <div className="flex-1">
            <Input
              type="phone-number"
              placeholder="Enter Primary Phone"
              label="Primary phone *"
              value={personalInfo.primaryPhone}
              onChange={(e) => handleChange('primaryPhone', e.target.value)}
              errorMsg={errors.primaryPhone}
            />
          </div>
        </div>
        <div className="px-4 md:px-12 pb-4 md:pb-6">
          <Dropdown
            label="Gender"
            placeholder="Select Gender"
            value={personalInfo.gender}
            setValue={(val) => handleChange('gender', val)}
            menuItems={[
              { label: 'Male', value: 'male' },
              { label: 'Female', value: 'female' },
            ]}
          />
        </div>
        <div className="flex flex-col md:flex-row px-4 md:px-12 pb-4 md:pb-6 gap-4">
          <div className="flex-1">
            <Calendar
              optional={true}
              disableFuture={true}
              label="Date Of Birth (MM-DD-YYYY) *"
              value={personalInfo.dateOfBirth || null}
              onChange={(newValue) => handleChange('dateOfBirth', newValue ? newValue.format('YYYY-MM-DD') : '')}
            />
            {errors.dateOfBirth && <p className="text-primary-static-error-600 text-sm mt-1 ml-4">{errors.dateOfBirth}</p>}
          </div>
          <div className="flex-1">
            <Input
              placeholder="XXX-XX-XXXX"
              label="Social Security Number *"
              value={personalInfo.ssn}
              onChange={(e) => handleChange('ssn', e.target.value)}
              errorMsg={errors.ssn}
            />
          </div>
        </div>
      </div>

      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full mt-4 md:mt-6">
        <div className="font-bold text-lg md:text-xl-20 pt-4 md:pt-6 pl-4 md:pl-12 pb-3 md:pb-4">
          Home Address
        </div>
        <div className="border-b border-divider mx-4 md:mx-6" />
        <div className="flex flex-col md:flex-row px-4 md:px-12 pt-4 md:pt-6 gap-4">
          <div className="w-full">
            <Input
              inputRef={addressRef}
              label="Address"
              placeholder="Enter your address..."
              value={addressInput}
              onChange={(e) => {
                setAddressInput(e.target.value)
                setPersonalInfo(prev => ({ ...prev, address: e.target.value }))
              }}
            />
          </div>
          <div className="w-full">
            <Input
              placeholder="34"
              label="Suite/Apt"
              value={personalInfo.aptNumber || ''}
              onChange={(e) => handleChange('aptNumber', e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col md:flex-row px-4 md:px-12 pt-4 md:pt-6 gap-4">
          <div className="flex-1">
            <Input
              label="City"
              placeholder="Enter City"
              value={personalInfo.city}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </div>
          <div className="flex-1">
            <Dropdown
              menuItems={states?.map((state) => ({
                label: `${state.shortName} - ${state.fullName}`,
                value: state.shortName,
              }))}
              label="State"
              placeholder="Select State"
              value={personalInfo.state}
              setValue={(val) => handleChange('state', val)}
            />
          </div>
        </div>
        <div className="flex px-4 md:px-12 pt-4 md:pt-6 pb-6 md:pb-12">
          <div className="w-full md:w-1/2">
            <Input
              label="Zip"
              placeholder="Enter Zip"
              value={personalInfo.zip}
              onChange={(e) => handleChange('zip', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full mt-4 md:mt-6">
        <div className="flex justify-between items-center py-4 md:py-6 px-4 md:px-12">
          <div className="font-bold text-lg md:text-xl-20">Co-Applicant</div>
          <Switch
            id="hasCoApplicant"
            checked={hasCoApplicant}
            onChange={(val) => setHasCoApplicant(val)}
            label=""
            size="md"
          />
        </div>
        {hasCoApplicant && (
          <>
            <div className="border-b border-divider mx-4 md:mx-6" />
            <div className="flex px-4 md:px-12 pt-4 md:pt-6 gap-4">
              <Button
                size="sm"
                type={coApplicantMode === 'create' ? "contained" : "outlined"}
                onClick={() => {
                  setCoApplicantMode('create')
                  setLinkedCoApplicantId(null)
                  setCoApplicantInfo({
                    firstName: '', lastName: '', middleName: '', email: '',
                    primaryPhone: '', ssNumber: '', dateOfBirth: '', gender: '',
                    address: '', aptNumber: '', city: '', state: '', zip: '',
                  })
                }}
              >
                Create New
              </Button>
              <Button
                size="sm"
                type={coApplicantMode === 'link' ? "contained" : "outlined"}
                onClick={() => {
                  setCoApplicantMode('link')
                  setLinkedCoApplicantId(null)
                  setCoApplicantInfo({
                    firstName: '', lastName: '', middleName: '', email: '',
                    primaryPhone: '', ssNumber: '', dateOfBirth: '', gender: '',
                    address: '', aptNumber: '', city: '', state: '', zip: '',
                  })
                }}
              >
                Link Existing
              </Button>
            </div>
            {coApplicantMode === 'link' && (
              <div className="px-4 md:px-12 pt-4 md:pt-6 pb-4 md:pb-6">
                <CoApplicantSearch company={personalInfo.company} onSelect={handleSelectExisting} />
              </div>
            )}
            {(coApplicantMode === 'create' || linkedCoApplicantId) && (
              <>
                <div className="flex flex-col md:flex-row px-4 md:px-12 pt-4 md:pt-6 gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Enter First Name"
                      label="First name *"
                      value={coApplicantInfo.firstName}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantChange('firstName', e.target.value)}
                      errorMsg={errors.coFirstName}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Enter Last Name"
                      label="Last name *"
                      value={coApplicantInfo.lastName}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantChange('lastName', e.target.value)}
                      errorMsg={errors.coLastName}
                    />
                  </div>
                </div>
                <div className="flex flex-col md:flex-row px-4 md:px-12 py-4 md:py-6 gap-4">
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder="Enter Email"
                      label="Email"
                      value={coApplicantInfo.email}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantChange('email', e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="phone-number"
                      placeholder="Enter Phone"
                      label="Phone"
                      value={coApplicantInfo.primaryPhone}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantChange('primaryPhone', e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex flex-col md:flex-row px-4 md:px-12 pb-4 md:pb-6 gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="XXX-XX-XXXX"
                      label="SSN"
                      value={coApplicantInfo.ssNumber}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantChange('ssNumber', e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Calendar
                      optional={true}
                      disableFuture={true}
                      label="Date Of Birth (MM-DD-YYYY) *"
                      value={coApplicantInfo.dateOfBirth || null}
                      disabled={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(newValue) => handleCoApplicantChange('dateOfBirth', newValue ? newValue.format('YYYY-MM-DD') : '')}
                    />
                    {errors.coDateOfBirth && <p className="text-primary-static-error-600 text-sm mt-1 ml-4">{errors.coDateOfBirth}</p>}
                  </div>
                </div>
                <div className="px-4 md:px-12 pb-4 md:pb-6">
                  <Dropdown
                    label="Gender"
                    placeholder="Select Gender"
                    value={coApplicantInfo.gender}
                    disabled={coApplicantMode === 'link' && !!linkedCoApplicantId}
                    setValue={(val) => handleCoApplicantChange('gender', val)}
                    menuItems={[
                      { label: 'Male', value: 'male' },
                      { label: 'Female', value: 'female' },
                    ]}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="mt-4 md:mt-6 mb-6 flex justify-end w-full">
        <Button
          width="w-full md:min-w-[156px] md:w-auto"
          onClick={handleNext}
          disabled={
            !personalInfo.dateOfBirth ||
            (hasCoApplicant && coApplicantMode === 'create' && !coApplicantInfo.dateOfBirth) ||
            (hasCoApplicant && coApplicantMode === 'link' && !linkedCoApplicantId)
          }
        >
          Save & proceed
        </Button>
      </div>
    </>
  )
}

export default PersonalInfoStep
